import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserGroupIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const WorkflowStats = ({ data, onDataChange, user }) => {
  const [stats, setStats] = useState({
    totalSubmissions: 0,
    pendingApprovals: 0,
    approvedCount: 0,
    rejectedCount: 0,
    averageApprovalTime: 0,
    workflowEfficiency: 0,
    monthlyTrends: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30'); // days

  useEffect(() => {
    loadWorkflowStats();
  }, [timeRange]);

  const loadWorkflowStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Load workflow statistics
      const response = await fetch(`/api/workflow/statistics?days=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data.data || {});
      } else {
        // Mock data for now since API doesn't exist yet
        setStats({
          totalSubmissions: 24,
          pendingApprovals: 8,
          approvedCount: 14,
          rejectedCount: 2,
          averageApprovalTime: 2.3,
          workflowEfficiency: 87.5,
          monthlyTrends: [
            { month: 'Jan', submissions: 18, approved: 15, rejected: 3 },
            { month: 'Feb', submissions: 22, approved: 19, rejected: 3 },
            { month: 'Mar', submissions: 24, approved: 20, rejected: 4 }
          ]
        });
      }
    } catch (error) {
      console.error('Error loading workflow statistics:', error);
      // Use mock data on error
      setStats({
        totalSubmissions: 24,
        pendingApprovals: 8,
        approvedCount: 14,
        rejectedCount: 2,
        averageApprovalTime: 2.3,
        workflowEfficiency: 87.5,
        monthlyTrends: [
          { month: 'Jan', submissions: 18, approved: 15, rejected: 3 },
          { month: 'Feb', submissions: 22, approved: 19, rejected: 3 },
          { month: 'Mar', submissions: 24, approved: 20, rejected: 4 }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4">
          <h3 className="text-lg font-medium text-gray-900">{value}</h3>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );

  const getEfficiencyColor = (efficiency) => {
    if (efficiency >= 90) return 'text-green-600 bg-green-100';
    if (efficiency >= 75) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Workflow Statistics</h2>
          <p className="text-sm text-gray-500">
            Performance metrics and analytics for approval workflows
          </p>
        </div>
        
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 3 months</option>
          <option value="365">Last year</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Submissions"
          value={stats.totalSubmissions}
          icon={ChartBarIcon}
          color="bg-blue-500"
          subtitle={`Last ${timeRange} days`}
        />
        
        <StatCard
          title="Pending Approvals"
          value={stats.pendingApprovals}
          icon={ClockIcon}
          color="bg-yellow-500"
          subtitle="Awaiting action"
        />
        
        <StatCard
          title="Approved"
          value={stats.approvedCount}
          icon={CheckCircleIcon}
          color="bg-green-500"
          subtitle={`${((stats.approvedCount / stats.totalSubmissions) * 100).toFixed(1)}% of total`}
        />
        
        <StatCard
          title="Rejected"
          value={stats.rejectedCount}
          icon={XCircleIcon}
          color="bg-red-500"
          subtitle={`${((stats.rejectedCount / stats.totalSubmissions) * 100).toFixed(1)}% of total`}
        />
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Average Approval Time */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <ClockIcon className="h-6 w-6 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900">Average Approval Time</h3>
          </div>
          
          <div className="text-center py-8">
            <div className="text-4xl font-bold text-blue-600 mb-2">
              {stats.averageApprovalTime}
            </div>
            <div className="text-sm text-gray-500">days</div>
            <div className="mt-4 text-sm text-gray-600">
              Time from submission to final approval
            </div>
          </div>
        </div>

        {/* Workflow Efficiency */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <ChartBarIcon className="h-6 w-6 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900">Workflow Efficiency</h3>
          </div>
          
          <div className="text-center py-8">
            <div className={`text-4xl font-bold mb-2 ${getEfficiencyColor(stats.workflowEfficiency)}`}>
              {stats.workflowEfficiency}%
            </div>
            <div className="text-sm text-gray-500">efficiency score</div>
            <div className="mt-4 text-sm text-gray-600">
              Based on completion rate and timing
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Trends */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <CalendarIcon className="h-6 w-6 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Monthly Trends</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-900">Month</th>
                <th className="text-center py-3 px-4 font-medium text-gray-900">Submissions</th>
                <th className="text-center py-3 px-4 font-medium text-gray-900">Approved</th>
                <th className="text-center py-3 px-4 font-medium text-gray-900">Rejected</th>
                <th className="text-center py-3 px-4 font-medium text-gray-900">Success Rate</th>
              </tr>
            </thead>
            <tbody>
              {stats.monthlyTrends.map((trend, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-3 px-4 text-sm font-medium text-gray-900">
                    {trend.month}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-900">
                    {trend.submissions}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-green-600">
                    {trend.approved}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-red-600">
                    {trend.rejected}
                  </td>
                  <td className="py-3 px-4 text-center text-sm">
                    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                      (trend.approved / trend.submissions) >= 0.9 
                        ? 'bg-green-100 text-green-800'
                        : (trend.approved / trend.submissions) >= 0.75
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {((trend.approved / trend.submissions) * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {stats.monthlyTrends.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p>No trend data available for the selected time range.</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-4">
          <UserGroupIcon className="h-6 w-6 text-blue-600" />
          <h3 className="text-lg font-medium text-blue-900">Quick Insights</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4">
            <div className="text-sm font-medium text-gray-900 mb-1">
              Approval Rate
            </div>
            <div className="text-2xl font-bold text-green-600">
              {stats.totalSubmissions > 0 
                ? ((stats.approvedCount / stats.totalSubmissions) * 100).toFixed(1)
                : 0
              }%
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.approvedCount} of {stats.totalSubmissions} submissions approved
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4">
            <div className="text-sm font-medium text-gray-900 mb-1">
              Pending Backlog
            </div>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.pendingApprovals}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.pendingApprovals > 5 ? 'Action needed' : 'Under control'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowStats;